import type Hls from "hls.js";
import type { ErrorData, Fragment, LevelDetails } from "hls.js";

import { loadHlsBundle } from "../live/hls.js";

/**
 * Что сейчас с воспроизведением. Каждое состояние показывается словами: самое
 * плохое, что было у прежнего проигрывателя, — щелчок, после которого ничего
 * не происходит и непонятно, грузится это или зависло.
 */
export type PlayState =
  | { kind: "idle" }
  /** `slow` — грузится дольше обычного: скорее всего, давняя запись из S3. */
  | { kind: "loading"; slow?: boolean }
  | { kind: "buffering" }
  | { kind: "playing" }
  | { kind: "paused" }
  /** Доиграли всё, что есть. */
  | { kind: "ended" }
  | { kind: "error"; text: string };

export interface PlayerEvents {
  state(state: PlayState): void;
  /** Время текущего кадра, мс Unix. `null` — кадра нет. */
  time(wallMs: number | null): void;
  /** Запрошенного момента в записи нет — показываем со следующего. */
  skipped(askedMs: number, shownMs: number): void;
  /** Сменился файл архива под бегунком (для «скачать»). */
  segment(id: number | null): void;
}

/**
 * Сколько ждать первую картинку, прежде чем честно сказать, что не вышло.
 *
 * Минута, а не секунды: первый просмотр давней записи, которой уже нет на
 * диске, заставляет сервер один раз пройти объект в S3 целиком, чтобы
 * разметить фрагменты. Дальше эта запись открывается сразу.
 */
const FIRST_FRAME_TIMEOUT_MS = 60_000;

/** Через сколько «загрузка…» сменяется объяснением, почему долго. */
const SLOW_AFTER_MS = 4_000;

/** Политика загрузки плейлиста: ждать столько же, сколько первую картинку. */
const PLAYLIST_POLICY = {
  default: {
    maxTimeToFirstByteMs: FIRST_FRAME_TIMEOUT_MS,
    maxLoadTimeMs: FIRST_FRAME_TIMEOUT_MS,
    timeoutRetry: { maxNumRetry: 0, retryDelayMs: 0, maxRetryDelayMs: 0 },
    errorRetry: { maxNumRetry: 1, retryDelayMs: 1000, maxRetryDelayMs: 8000 },
  },
};

/**
 * Проигрыватель архива.
 *
 * Играет HLS-плейлист, который сервер собирает поверх записанных fMP4 (см.
 * `app/playlist.rs`). Сырой файл в `<video src>` браузер считал
 * односекундным; через hls.js длительность точная, перемотка работает, и
 * запись идёт подряд через границы файлов.
 *
 * Всё время наружу — стенное, в миллисекундах Unix. Внутренняя шкала hls.js
 * выбрасывает дыры между файлами, и отдавать её странице значило бы заставить
 * её гадать, где какая дыра.
 */
export class ArchivePlayer {
  private hls: Hls | null = null;
  private details: LevelDetails | null = null;
  private generation = 0;
  /** Чья запись. Сменили камеру — следующий щелчок откроет новый плейлист. */
  private camera = "";
  private timeout: number | null = null;
  private slow: number | null = null;
  private rate = 1;
  private lastSegment: number | null = null;
  /** С какого момента просили последнее продолжение. */
  private continuedFrom: number | null = null;

  constructor(
    private readonly video: HTMLVideoElement,
    private readonly on: PlayerEvents,
  ) {
    video.addEventListener("waiting", () => this.emitIfCurrent({ kind: "buffering" }));
    video.addEventListener("playing", () => {
      this.clearTimeout();
      this.emitIfCurrent({ kind: "playing" });
    });
    video.addEventListener("pause", () => {
      if (!video.ended) this.emitIfCurrent({ kind: "paused" });
    });
    video.addEventListener("ended", () => void this.continueAfterWindow());
    video.addEventListener("timeupdate", () => this.reportTime());
    video.addEventListener("seeking", () => this.reportTime());
  }

  /**
   * Начать с момента `wallMs`. Прежний запрос, если ещё грузится, отменяется.
   *
   * `continuing` — это продолжение после конца прошлого окна. Тогда «записи
   * нет» (404) — не ошибка, а конец записи, и о нём говорится как о конце.
   */
  async open(camera: string, wallMs: number, continuing = false): Promise<void> {
    const generation = ++this.generation;
    if (!continuing) this.continuedFrom = null;
    this.camera = camera;
    this.teardown();
    this.on.state({ kind: "loading" });
    this.armTimeout(generation);

    let HlsClass: typeof Hls;
    try {
      HlsClass = await loadHlsBundle();
    } catch {
      this.fail(generation, "не загрузился проигрыватель — обновите страницу");
      return;
    }
    if (generation !== this.generation) return;
    if (!HlsClass.isSupported()) {
      this.fail(generation, "браузер не умеет воспроизводить архив (нет MSE)");
      return;
    }

    const hls = new HlsClass({
      autoStartLoad: false,
      // Архив — не прямой эфир: никаких догоняний края и ускорений.
      lowLatencyMode: false,
      // Вставок (interstitials) в архиве нет, а их контроллер повторно
      // вызывал startLoad после первого куска. Каждый такой вызов рвёт цепочку
      // кадров, и следующий кусок — у MediaMTX он не начинается с ключевого
      // кадра — hls.js отбрасывал и откатывался на предыдущий, по кругу.
      enableInterstitialPlayback: false,
      backBufferLength: 60,
      maxBufferLength: 30,
      manifestLoadPolicy: PLAYLIST_POLICY,
      playlistLoadPolicy: PLAYLIST_POLICY,
    });
    this.hls = hls;

    let started = false;
    hls.on(HlsClass.Events.LEVEL_LOADED, (_event, data) => {
      if (generation !== this.generation) return;
      this.details = data.details;
      if (started) return;
      started = true;

      const position = positionOf(data.details, wallMs);
      if (position !== null) {
        hls.startLoad(position);
      } else {
        // Момента в плейлисте нет. Сервер отдаёт его с запасом назад, так
        // что начинать с первого куска нельзя — это показало бы запись до
        // щелчка. Берём ближайшую запись после: за дырой — следующую (и
        // говорим об этом), а за краем растущей записи — её последний кусок.
        const frags = data.details.fragments;
        const after = frags.find((f) => (f.programDateTime ?? 0) >= wallMs);
        // После момента ничего, а плейлист закрыт — запись здесь кончилась.
        // Начать с хвоста значило бы крутить последние секунды по кругу.
        if (!after && !data.details.live) {
          this.finish(generation);
          return;
        }
        const chosen = after ?? frags[frags.length - 1];
        const shown = chosen?.programDateTime ?? wallMs;
        if (after && shown - wallMs > 2000) this.on.skipped(wallMs, shown);
        hls.startLoad(chosen?.start ?? 0);
      }
      this.video.playbackRate = this.rate;
      void this.video.play().catch(() => {});
    });
    hls.on(HlsClass.Events.FRAG_CHANGED, (_event, data) => {
      if (generation !== this.generation) return;
      const id = segmentIdOf(data.frag);
      if (id !== this.lastSegment) {
        this.lastSegment = id;
        this.on.segment(id);
      }
    });
    hls.on(HlsClass.Events.ERROR, (_event, data) => {
      if (generation !== this.generation || !data.fatal) return;
      if (continuing && !started && data.response?.code === 404) {
        this.finish(generation);
        return;
      }
      this.fail(generation, describe(data));
    });

    hls.loadSource(
      `/api/archive/${encodeURIComponent(camera)}/playlist.m3u8` +
        `?from=${encodeURIComponent(new Date(wallMs).toISOString())}`,
    );
    hls.attachMedia(this.video);
  }

  /**
   * Перейти к моменту. Внутри загруженного окна — простой сдвиг, без
   * перезагрузки; за его пределами — новый плейлист.
   */
  seek(wallMs: number): void {
    const position = this.details ? positionOf(this.details, wallMs) : null;
    if (position !== null && this.hls) {
      this.video.currentTime = position;
      if (this.video.paused) void this.video.play().catch(() => {});
      return;
    }
    if (this.camera !== "") void this.open(this.camera, wallMs);
  }

  /** Сдвинуться на `seconds` от текущего кадра. */
  step(seconds: number): void {
    const now = this.currentWall();
    if (now !== null) this.seek(now + seconds * 1000);
  }

  toggle(): void {
    if (!this.hls) return;
    if (this.video.paused) void this.video.play().catch(() => {});
    else this.video.pause();
  }

  setRate(rate: number): void {
    this.rate = rate;
    this.video.playbackRate = rate;
  }

  currentWall(): number | null {
    return this.hls?.playingDate?.getTime() ?? null;
  }

  /** Сменить камеру: текущее воспроизведение к ней не относится. */
  use(camera: string): void {
    if (camera === this.camera) return;
    this.stop();
    this.camera = camera;
  }

  /** Остановить и отпустить сеть: при уходе с вкладки и при смене камеры. */
  stop(): void {
    this.generation++;
    this.teardown();
    this.on.state({ kind: "idle" });
    this.on.time(null);
  }

  private teardown(): void {
    this.clearTimeout();
    this.hls?.destroy();
    this.hls = null;
    this.details = null;
    this.lastSegment = null;
    this.on.segment(null);
    this.video.removeAttribute("src");
    this.video.load();
  }

  /**
   * Окно плейлиста кончилось. Если запись идёт дальше — следующее окно с того
   * места, где кончилось это; иначе честно говорим, что дальше записи нет.
   */
  private async continueAfterWindow(): Promise<void> {
    const details = this.details;
    const last = details?.fragments[details.fragments.length - 1];
    if (!last || last.programDateTime === null || details?.live !== false) {
      this.emitIfCurrent({ kind: "ended" });
      return;
    }
    const next = last.programDateTime + last.duration * 1000;
    // Продолжение не продвинулось — сервер отдал то же окно, и просить его
    // снова значило бы крутиться на месте. Лучше честно сказать «дальше нет».
    if (this.continuedFrom !== null && next <= this.continuedFrom + 500) {
      this.emitIfCurrent({ kind: "ended" });
      return;
    }
    this.continuedFrom = next;
    // Сразу следующее окно, без проверочного запроса: сервер строит плейлист
    // не бесплатно, и делать это дважды незачем. 404 разберёт `open`.
    void this.open(this.camera, next, true);
  }

  private reportTime(): void {
    if (!this.hls) return;
    this.on.time(this.currentWall());
  }

  private emitIfCurrent(state: PlayState): void {
    if (this.hls) this.on.state(state);
  }

  private armTimeout(generation: number): void {
    this.clearTimeout();
    this.timeout = window.setTimeout(() => {
      if (generation !== this.generation) return;
      if (this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        this.fail(generation, "архив не ответил за минуту");
      }
    }, FIRST_FRAME_TIMEOUT_MS);
    this.slow = window.setTimeout(() => {
      if (generation !== this.generation) return;
      if (this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        this.on.state({ kind: "loading", slow: true });
      }
    }, SLOW_AFTER_MS);
  }

  private clearTimeout(): void {
    if (this.timeout !== null) window.clearTimeout(this.timeout);
    if (this.slow !== null) window.clearTimeout(this.slow);
    this.timeout = null;
    this.slow = null;
  }

  /** Запись кончилась — это не ошибка, а то, что есть. */
  private finish(generation: number): void {
    if (generation !== this.generation) return;
    this.clearTimeout();
    this.hls?.destroy();
    this.hls = null;
    this.on.state({ kind: "ended" });
  }

  private fail(generation: number, text: string): void {
    if (generation !== this.generation) return;
    this.clearTimeout();
    this.hls?.destroy();
    this.hls = null;
    this.on.state({ kind: "error", text });
  }
}

/** Позиция на шкале hls.js для стенного момента, если он внутри плейлиста. */
function positionOf(details: LevelDetails, wallMs: number): number | null {
  for (const frag of details.fragments) {
    const pdt = frag.programDateTime;
    if (pdt === null) continue;
    if (wallMs >= pdt && wallMs < pdt + frag.duration * 1000) {
      return frag.start + (wallMs - pdt) / 1000;
    }
  }
  return null;
}

function segmentIdOf(frag: Fragment): number | null {
  const match = /\/api\/archive\/segment\/(\d+)/.exec(frag.url);
  return match ? Number(match[1]) : null;
}

/** Фатальная ошибка hls.js — словами, которые что-то говорят человеку. */
function describe(data: ErrorData): string {
  const code = data.response?.code ?? 0;
  if (code === 401) return "сессия истекла — войдите снова";
  if (code === 403) return "нет доступа к архиву";
  if (code === 404) return "с этого момента записи нет";
  if (code === 410) return "запись удалена по сроку хранения";
  if (code === 503) return "архив занят другими просмотрами — попробуйте через минуту";
  if (code === 502 || code === 504) return "хранилище архива не отвечает";
  if (data.type === "networkError") return "архив не отвечает — проверьте связь";
  if (data.type === "mediaError") return "запись не удалось декодировать";
  return `не удалось воспроизвести (${data.details})`;
}
